import React, { useMemo, memo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import ProfileMenu from 'react/containers/layout/Header/ProfileMenu';
import RightOptionsItem from './RightOptionsItem';

import { formatDate, getResourceTo } from 'react/utils/utils';
import { actionCreators } from 'react/api/actions/header';
import Applications from 'react/containers/layout/Header/Applications';
import ActivityCenterContainer from '../ActivityCenter/ActivityCenterContainer';

function RightOptions({ applications }) {
  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const downloadsActive = useSelector((state) => state.bulkDownload.getIn(['downloads']));

  const photoDownloadItems = useMemo(() => {
    if (!downloadsActive) return [];

    return downloadsActive.keySeq().toArray().map((key) => {
      const download = downloadsActive.get(key);
      const isPending = download.get('status') === 'PROGRESS';

      return {
        id: key,
        type: 'photo',
        isPending,
        urlFile: download.get('urlFile'),
        totalFiles: download.get('totalFiles'),
        date: formatDate(new Date())
      };
    });
  }, [downloadsActive]);


  const activeCenterBadge = useMemo(
    () => photoDownloadItems?.length > 0 ? photoDownloadItems.length : null,
    [photoDownloadItems]
  );

  const isDsgUser = useMemo(() => {
    const user = Monet.Session.User;

    return user?.externalUser && user?.dsgUserProfile;
  }, [Monet.Session.User]);

  const welcomeText = useMemo(() => {
    const welcomeTextTranslated = getResourceTo('topMenu.UserProfile.Hello');
    const userName = Monet.Session.User?.name;

    return `${welcomeTextTranslated} ${userName}`;
  }, [Monet.Session.User]);

  const userEmailText = useMemo(() => {
    const userName = Monet.Session.User?.email ?? '';

    return userName;
  }, [Monet.Session.User]);

  const useGroupsText = useMemo(() => {
    const userGroups = isDsgUser
      ? Monet.Session.User?.dsgUserProfile?.Roles ?? []
      : Monet.Session.User?.groups ?? [];

    return userGroups.map(group => (typeof group === 'string' ? group : group.name)).join(', ');
  }, [Monet.Session.User, isDsgUser]);

  const helpOnClick = () => {
    const help = new Monet.UI.Help('', Monet.I18n.getResourcesByKey('docs'));
    help.logIn();
  };

  function onWindowClick(e) {
    const element = containerRef?.current;

    if (!element) return;

    if (!element.contains(e.target)) {
      dispatch(actionCreators.selectRightOptionsItem(null));
    }
  }

  useEffect(() => {
    document.addEventListener('click', onWindowClick);

    return () => document.removeEventListener('click', onWindowClick);
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid="headerRightOptionsContainer"
      id="headerRightOptionsContainer"
    >
      <RightOptionsItem
        className="headerActivityCenter"
        name="ActivityCenter"
        totalBadge={activeCenterBadge}
      >
        <ActivityCenterContainer items={photoDownloadItems} />
      </RightOptionsItem>
      <RightOptionsItem
        title="Descartes suite applications"
        className="headerApplications"
        name="headerApplication"
        display={!!applications?.length}
      >
        <Applications applications={applications} />
      </RightOptionsItem>
      <RightOptionsItem
        className="headerHelp"
        name="headerHelp"
        onClick={helpOnClick}
      />
      <RightOptionsItem
        className="headerUserProfile"
        name="userProfile"
        title={welcomeText}
        email={userEmailText}
        group={useGroupsText}
        isDsgUser={isDsgUser}
      >
        <ProfileMenu isDsgUser={isDsgUser} />
      </RightOptionsItem>
    </div>
  );
}

RightOptions.propTypes = {
  applications: PropTypes.array.isRequired
};

export default memo(RightOptions);
